import { User, GraduationCap, Languages, Briefcase, MapPin, Check } from "lucide-react";
import { PROVINCES, EDUCATION_OPTIONS, TEER_OPTIONS, FRENCH_OPTIONS, LEGAL_STATUS_OPTIONS, REGIONS_BY_PROVINCE, TEST_TYPES, FRENCH_TESTS, CONNECTION_TYPES } from "@/lib/constants";
import { NocAutocomplete } from "@/components/NocAutocomplete";
import { Field, CheckRow, SectionTitle, inputClass } from "@/components/assessment/fields";

export function PersonalStep({ profile, set, show }) {
  return (
    <div className="space-y-6">
      <SectionTitle icon={User} title="Personal details" />
      <div className="grid gap-5 sm:grid-cols-2">
        {show("age") && (
          <Field label="Age" hint="Your current age in years.">
            <input type="number" min={17} max={65} value={profile.age} data-testid="input-age"
              onChange={(e) => set({ age: e.target.value })} className={inputClass} />
          </Field>
        )}
        {show("spouse") && (
          <Field label="Marital status">
            <select value={profile.maritalStatus} data-testid="select-marital-status"
              onChange={(e) => set({ maritalStatus: e.target.value })} className={inputClass}>
              <option value="single">Single / not married</option>
              <option value="married">Married / common-law</option>
            </select>
          </Field>
        )}
        {show("funds") && (
          <Field label="Settlement funds (CAD)" hint="Liquid funds available to settle.">
            <input type="number" min={0} step={1000} value={profile.funds} data-testid="input-funds"
              onChange={(e) => set({ funds: e.target.value })} className={inputClass} />
          </Field>
        )}
        {show("legalStatus") && (
          <Field label="Current status in Canada" hint="Alberta, Saskatchewan and Ontario employer streams require or score a valid work permit.">
            <select value={profile.legalStatus || "none"} data-testid="select-legal-status"
              onChange={(e) => set({ legalStatus: e.target.value })} className={inputClass}>
              {LEGAL_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
        )}
      </div>
      {show("spouse") && profile.maritalStatus === "married" && (
        <CheckRow checked={profile.spouseAccompanying} onChange={(v) => set({ spouseAccompanying: v })}
          testId="check-spouse-accompanying" label="My spouse / partner will accompany me to Canada" />
      )}
      {show("sibling") && (
        <CheckRow checked={profile.siblingInCanada} onChange={(v) => set({ siblingInCanada: v })}
          testId="check-sibling" label="I have a sibling (Canadian citizen or PR) living in Canada" />
      )}
    </div>
  );
}

export function EducationStep({ profile, set, show, spouseOn, setSpouse }) {
  return (
    <div className="space-y-6">
      <SectionTitle icon={GraduationCap} title="Education" />
      {show("education") && (
        <Field label="Highest level of education">
          <select value={profile.education} data-testid="select-education"
            onChange={(e) => set({ education: e.target.value })} className={inputClass}>
            {EDUCATION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
      )}
      {show("eduInCanada") && (
        <CheckRow checked={profile.eduInCanada} onChange={(v) => set({ eduInCanada: v })}
          testId="check-edu-canada" label="I completed a post-secondary credential in Canada" />
      )}
      {spouseOn && (
        <Field label="Spouse's highest education">
          <select value={profile.spouse?.education || "none"} data-testid="select-spouse-education"
            onChange={(e) => setSpouse({ education: e.target.value })} className={inputClass}>
            {EDUCATION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
      )}
    </div>
  );
}

const ABILITIES = ["listening", "reading", "writing", "speaking"];
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

function FrenchFields({ profile, set, setFrench }) {
  const test = FRENCH_TESTS.find((t) => t.value === (profile.frenchTest || "clb")) || FRENCH_TESTS[0];
  return (
    <div className="space-y-5 rounded-lg border border-slate-200 bg-slate-50/60 p-4">
      <Field label="French ability (optional)" hint="Enter official TEF Canada or TCF Canada scores and we convert them to NCLC per ability, or pick an approximate level.">
        <select value={profile.frenchTest || "clb"} data-testid="select-french-test"
          onChange={(e) => set({ frenchTest: e.target.value })} className={inputClass}>
          {FRENCH_TESTS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </Field>
      {test.value === "clb" ? (
        <Field label="Approximate French level" hint="French earns CRS bonus points and counts as a second official language in several provincial grids.">
          <select value={profile.frenchCLB} data-testid="select-french"
            onChange={(e) => set({ frenchCLB: e.target.value })} className={inputClass}>
            {FRENCH_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2">
          {ABILITIES.map((ab) => (
            <Field key={ab} label={`${cap(ab)} (${test.label})`}>
              <input type="number" min={test.ranges[ab][0]} max={test.ranges[ab][1]} step={1} placeholder={test.ph[ab]}
                value={profile.french?.[ab] ?? ""} data-testid={`input-french-${ab}`}
                onChange={(e) => setFrench(ab, e.target.value)} className={inputClass} />
            </Field>
          ))}
        </div>
      )}
    </div>
  );
}

export function LanguageStep({ profile, set, show, spouseOn, setSpouse, setLang, setFrench, langRange }) {
  return (
    <div className="space-y-6">
      <SectionTitle icon={Languages} title="Language ability" />
      {show("language") && (
        <>
          <Field label="English test type" hint="CELPIP scores map directly to CLB; IELTS bands and PTE Core scores (10–90) are converted using IRCC's equivalency tables.">
            <select value={profile.testType} data-testid="select-test-type"
              onChange={(e) => set({ testType: e.target.value })} className={inputClass}>
              {TEST_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Field>
          <div className="grid gap-5 sm:grid-cols-2">
            {["listening", "reading", "writing", "speaking"].map((ab) => (
              <Field key={ab} label={ab.charAt(0).toUpperCase() + ab.slice(1)}>
                <input type="number" min={langRange.min} max={langRange.max} step={langRange.stepv}
                  placeholder={langRange.ph} value={profile.language?.[ab] ?? ""}
                  data-testid={`input-lang-${ab}`}
                  onChange={(e) => setLang(ab, e.target.value)} className={inputClass} />
              </Field>
            ))}
          </div>
        </>
      )}
      {show("french") && <FrenchFields profile={profile} set={set} setFrench={setFrench} />}
      {spouseOn && <SpouseLanguageFields spouse={profile.spouse || {}} setSpouse={setSpouse} />}
    </div>
  );
}

const SPOUSE_TESTS = [{ value: "clb", label: "Approximate CLB (no test)" }, ...TEST_TYPES];

function SpouseLanguageFields({ spouse, setSpouse }) {
  const test = TEST_TYPES.find((t) => t.value === spouse.testType);
  const setScore = (ab, v) => setSpouse({ language: { ...(spouse.language || {}), [ab]: v } });
  return (
    <div className="space-y-5 rounded-lg border border-slate-200 bg-slate-50/60 p-4">
      <Field label="Spouse's English test" hint="Spouse language is worth up to 20 CRS points (5 per ability at CLB 9+).">
        <select value={spouse.testType || "clb"} data-testid="select-spouse-test"
          onChange={(e) => setSpouse({ testType: e.target.value })} className={inputClass}>
          {SPOUSE_TESTS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </Field>
      {test ? (
        <div className="grid gap-5 sm:grid-cols-2">
          {ABILITIES.map((ab) => (
            <Field key={ab} label={`Spouse ${ab} (${test.label})`}>
              <input type="number" min={test.min} max={test.max} step={test.step} placeholder={test.ph}
                value={spouse.language?.[ab] ?? ""} data-testid={`input-spouse-${ab}`}
                onChange={(e) => setScore(ab, e.target.value)} className={inputClass} />
            </Field>
          ))}
        </div>
      ) : (
        <Field label="Spouse's English CLB (approx.)">
          <input type="number" min={0} max={10} value={spouse.clb ?? 0} data-testid="input-spouse-clb"
            onChange={(e) => setSpouse({ clb: e.target.value })} className={inputClass} />
        </Field>
      )}
    </div>
  );
}

export function WorkStep({ profile, set, show, spouseOn, setSpouse }) {
  return (
    <div className="space-y-6">
      <SectionTitle icon={Briefcase} title="Work & occupation" />
      {show("experience") && (
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Foreign skilled work experience (years)">
            <input type="number" min={0} max={30} value={profile.foreignExp} data-testid="input-foreign-exp"
              onChange={(e) => set({ foreignExp: e.target.value })} className={inputClass} />
          </Field>
          <Field label="Canadian skilled work experience (years)">
            <input type="number" min={0} max={30} value={profile.canadianExp} data-testid="input-canadian-exp"
              onChange={(e) => set({ canadianExp: e.target.value })} className={inputClass} />
          </Field>
          <div className="sm:col-span-2">
            <CheckRow checked={!!profile.tradeCertificate} onChange={(v) => set({ tradeCertificate: v })} testId="check-trade-certificate"
              label="I hold a Canadian certificate of qualification in a skilled trade (issued by a province or territory)" />
          </div>
        </div>
      )}
      <Field label="Occupation / job title (optional)" hint="Start typing to match an official NOC 2021 title — picking one auto-fills your TEER level and tailors job-search links.">
        <NocAutocomplete value={profile.occupationTitle} nocCode={profile.nocCode} className={inputClass}
          onChange={(v) => set({ occupationTitle: v, nocCode: "" })}
          onPick={(it) => set({ occupationTitle: it.title, nocCode: it.code, noc_teer: it.teer })} />
      </Field>
      {show("earnings") && Number(profile.canadianExp) > 0 && (
        <Field label="Highest annual Canadian earnings (CAD, optional)" hint="From a CRA Notice of Assessment in the last 5 years — scored by Ontario's Workforce Priority Stream (up to 10 points at $80,000+ since Oct 5, 2026).">
          <input type="number" min={0} step={1000} value={profile.canadianEarnings ?? ""} data-testid="input-canadian-earnings"
            placeholder="e.g. 55000" onChange={(e) => set({ canadianEarnings: e.target.value })} className={inputClass} />
        </Field>
      )}
      {show("teer") && (
        <Field label="Occupation skill level (NOC TEER)">
          <select value={profile.noc_teer} data-testid="select-teer"
            onChange={(e) => set({ noc_teer: e.target.value })} className={inputClass}>
            {TEER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
      )}
      {show("occupationInDemand") && (
        <CheckRow checked={profile.occupationInDemand} onChange={(v) => set({ occupationInDemand: v })}
          testId="check-in-demand" label="My occupation is on a provincial in-demand / priority list" />
      )}
      {spouseOn && (
        <Field label="Spouse's Canadian work experience (years)">
          <input type="number" min={0} max={20} value={profile.spouse?.canadianExp ?? 0}
            data-testid="input-spouse-exp"
            onChange={(e) => setSpouse({ canadianExp: e.target.value })} className={inputClass} />
        </Field>
      )}
    </div>
  );
}

export function ConnectionsStep({ profile, set, show, toggleConnection, setConnectionType, streamProvince }) {
  return (
    <div className="space-y-6">
      <SectionTitle icon={MapPin} title="Provincial connections & job offer" />
      <ProvinceConnections profile={profile} show={show} toggleConnection={toggleConnection} setConnectionType={setConnectionType} />
      <EduProvinceField profile={profile} set={set} show={show} />
      <JobOfferFields profile={profile} set={set} show={show} streamProvince={streamProvince} />
    </div>
  );
}

function ConnectionTypeRows({ profile, setConnectionType }) {
  const selected = PROVINCES.filter((p) => (profile.provincialConnection || []).includes(p.code));
  if (!selected.length) return null;
  return (
    <div className="mt-4 space-y-3" data-testid="connection-types">
      {selected.map((p) => (
        <Field key={p.code} label={`What connections do you have to ${p.name}?`} hint="Select all that apply.">
          <div className="grid gap-2 sm:grid-cols-2" data-testid={`connection-type-${p.code}`}>
            {CONNECTION_TYPES.map((c) => {
              const on = [].concat(profile.connectionTypes?.[p.code] || []).includes(c.value);
              return (
                <button key={c.value} type="button" data-testid={`connection-type-${p.code}-${c.value}`} aria-pressed={on}
                  onClick={() => setConnectionType(p.code, c.value)}
                  className={`flex min-h-[48px] items-center gap-2.5 rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                    on ? "border-[#C8102E] bg-[#FEF2F2] text-[#C8102E]" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                  }`}>
                  <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${on ? "border-[#C8102E] bg-[#C8102E] text-white" : "border-slate-300 bg-white"}`}>
                    {on && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                  </span>
                  {c.short}
                </button>
              );
            })}
          </div>
        </Field>
      ))}
    </div>
  );
}

function ProvinceConnections({ profile, show, toggleConnection, setConnectionType }) {
  if (!show("connection")) return null;
  return (
    <Field label="Provinces where you have a connection" hint="Select any province where you have close relatives, friends, or prior study, work or residence — then tell us the type of connection. Manitoba and Saskatchewan score close relatives much higher than other ties.">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {PROVINCES.map((p) => {
          const on = (profile.provincialConnection || []).includes(p.code);
          return (
            <button key={p.code} type="button" data-testid={`connection-${p.code}`}
              onClick={() => toggleConnection(p.code)}
              className={`rounded-lg border px-3 py-2.5 text-sm font-semibold transition-colors ${
                on ? "border-[#C8102E] bg-[#FEF2F2] text-[#C8102E]" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
              }`}>
              {p.code} · {p.name}
            </button>
          );
        })}
      </div>
      <ConnectionTypeRows profile={profile} setConnectionType={setConnectionType} />
    </Field>
  );
}

function EduProvinceField({ profile, set, show }) {
  if (!show("connection") && !show("eduInCanada")) return null;
  return (
    <Field label="Province where you completed Canadian education" hint="Graduate streams (Manitoba IES, Nova Scotia Graduate, NB Graduates, B.C./Alberta PGWP rules) depend on where you studied. Counts as a provincial connection.">
      <select value={profile.eduInCanada ? (profile.eduProvince || "") : ""} data-testid="select-edu-province" disabled={!profile.eduInCanada}
        onChange={(e) => set({ eduProvince: e.target.value })} className={`${inputClass} disabled:bg-slate-50 disabled:text-slate-400`}>
        <option value="">{profile.eduInCanada ? "Select province" : "Tick “I completed a post-secondary credential in Canada” in Education first"}</option>
        {PROVINCES.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
      </select>
    </Field>
  );
}

const WAGE_HINTS = { BC: "BC SIRS scores wages from $16 to $70+/hr (up to 55 points).", ON: "Ontario Workforce Priority Stream scores $20 → $40+/hr." };
const REGION_LABELS = { BC: "Job location in B.C.", ON: "Job location in Ontario" };

function WageAndRegionFields({ profile, set, province }) {
  const regions = REGIONS_BY_PROVINCE[province];
  if (!regions) return null;
  return (
    <>
      <Field label="Hourly wage in the job offer (CAD)" hint={WAGE_HINTS[province]}>
        <input type="number" min={0} step={0.5} value={profile.jobOfferWage ?? ""} data-testid="input-job-wage"
          placeholder="e.g. 32.50" onChange={(e) => set({ jobOfferWage: e.target.value })} className={inputClass} />
      </Field>
      <Field label={REGION_LABELS[province]} hint="Regional jobs earn up to 15 extra points.">
        <select value={profile.jobOfferRegion || ""} data-testid="select-job-region"
          onChange={(e) => set({ jobOfferRegion: e.target.value })} className={inputClass}>
          {regions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>
    </>
  );
}

function JobOfferDetails({ profile, set, show, streamProvince }) {
  return (
    <div className="grid gap-5 rounded-lg border border-slate-200 bg-slate-50/60 p-4 sm:grid-cols-2">
      <Field label="Job offer province">
        <select value={profile.jobOfferProvince} data-testid="select-job-province"
          onChange={(e) => set({ jobOfferProvince: e.target.value })} className={inputClass}>
          <option value="">Select province</option>
          {PROVINCES.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
        </select>
      </Field>
      <Field label="Job offer skill level (TEER)">
        <select value={profile.jobOfferTeer} data-testid="select-job-teer"
          onChange={(e) => set({ jobOfferTeer: e.target.value })} className={inputClass}>
          {TEER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>
      {show("jobOfferDetails") && (
        <>
          <Field label="Months worked full-time in this position" hint="With this employer, in this role. 0 if you haven't started. Used by B.C., Alberta, Saskatchewan, Manitoba, Nova Scotia and Ontario employer streams.">
            <input type="number" min={0} max={120} value={profile.jobOfferMonths ?? 0} data-testid="input-job-months"
              onChange={(e) => set({ jobOfferMonths: e.target.value })} className={inputClass} />
          </Field>
          <WageAndRegionFields profile={profile} set={set} province={profile.jobOfferProvince || streamProvince} />
        </>
      )}
    </div>
  );
}

function JobOfferFields({ profile, set, show, streamProvince }) {
  if (!show("jobOffer")) return null;
  return (
    <>
      <CheckRow checked={profile.jobOffer} onChange={(v) => set({ jobOffer: v, jobOfferTeer: v ? (profile.jobOfferTeer >= 0 ? profile.jobOfferTeer : 1) : -1 })}
        testId="check-job-offer" label="I have a valid job offer from a Canadian employer" />
      {profile.jobOffer && <JobOfferDetails profile={profile} set={set} show={show} streamProvince={streamProvince} />}
    </>
  );
}


export const STEP_COMPONENTS = { personal: PersonalStep, education: EducationStep, language: LanguageStep, work: WorkStep, connections: ConnectionsStep };
